import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import {
  map,
  catchError,
  switchMap,
  tap,
  mergeMap,
  delay,
} from 'rxjs/operators';
import {filter, of, withLatestFrom, from} from 'rxjs';
import { SearchService } from '../../services/search.service';
import { loadCredits } from '../subscription/subscription.actions';
import {
  allSearches,
  allSearchesSuccess,
  allSearchesFailure,
  newSearch,
  newSearchSuccess,
  newSearchFailure,
  fetchReportFailure,
  fetchReportSuccess,
  fetchReport,
  allNotes,
  allNotesFailure,
  allNotesSuccess,
  assistantHistory,
  assistantHistorySuccess,
  assistantHistoryFailure,
  askAssistant,
  askAssistantSuccess,
  askAssistantFailure,
  deleteNote,
  deleteNoteSuccess,
  deleteNoteFailure,
  createNote,
  createNoteSuccess,
  createNoteFailure,
  getNotesSummary,
  getNotesSummarySuccess,
  getNotesSummaryFailure,
  showMoreNotes,
  showMoreNotesSuccess,
  showMoreNotesFailure,
  editNote,
  editNoteSuccess,
  editNoteFailure,
  fetchSelfReport,
  fetchSelfReportSuccess,
  fetchSelfReportFailure,
  archiveReport,
  archiveReportSuccess,
  archiveReportFailure,
  restoreReport,
  restoreReportSuccess,
  fuzzySearchFailure,
  fuzzySearchSuccess,
  fuzzySearch, enrichProfileFailure, enrichProfileSuccess, enrichProfile, updateReport,
  generateInsights, generateInsightsSuccess, generateInsightsFailure,
  updateRevenue, updateRevenueSuccess, updateRevenueFailure,
  updateContactInfo, updateContactInfoSuccess, updateContactInfoFailure
} from './search.actions';
import { HubspotService } from '../../services/hubspot.service';
import { NoteService } from '../../services/note.service';
import { HttpHeaders } from '@angular/common/http';
import {selectReport} from "./search.selectors";
import {Store} from "@ngrx/store";

@Injectable()
export class SearchEffects {
  allSearches$ = createEffect(() =>
    this.actions$.pipe(
      ofType(allSearches),
      switchMap(({ page, perPage, search, tags, archived, workspaceId, sortBy, sortDir, dateRange }) => {
        return this.searchService
          .allSearches(page, perPage, search, tags, archived, workspaceId, sortBy, sortDir, dateRange)
          .pipe(
            // delay(500), // Artificial delay to simulate network latency
            map((response) => {
              if (!response || !response.searches) {
                // If the response object is not structured as expected, throw an error
                throw new Error('Invalid all searches response');
              }
              // console.log('All searches successful', response.searches);
              return allSearchesSuccess({ searches: response.searches });
            }),
            catchError((error) => {
              console.error('[Effect] Searches failed with error:', error);
              return of(allSearchesFailure({ error }));
            })
          );
      })
    )
  );

  newSearch$ = createEffect(() => this.actions$.pipe(
    ofType(newSearch),
    // tap(action => console.log('Effect started with action:', action)),
    mergeMap(action =>
      this.searchService.newSearch(action.user_id, action.linkedin_profile, action.workspace_id).pipe(
        // tap(data => console.log('API call successful, data:', data)),
        map(data => newSearchSuccess({ search: data })),
        catchError(error => {
          console.error('API call failed, error:', error);
          return of(newSearchFailure({ error }));
        })
      )
    ),
    tap({
      // next: success => console.log('Mapping data to success action', success),
      error: err => console.error('Error in effect processing:', err)
    })
  ));

  // Automatically refresh credits after successful search
  refreshCreditsAfterNewSearch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(newSearchSuccess),
      map(() => {
        return loadCredits();
      })
    )
  );

  /*
  newSearch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(newSearch),
      // tap(action => console.log('Effect started with action:', action)),
      mergeMap((action) =>
        this.searchService
          .newSearch(action.user_id, action.linkedin_profile)
          .pipe(
            // tap(data => console.log('API call successful, data:', data)),
            map((data) => newSearchSuccess({ search: data })), // Ensure data.searches matches expected data structure
            catchError((error) => {
              console.error('API call failed, error:', error);
              return of(newSearchFailure({ error }));
            })
          )
      ),
      tap({
        // next: success => console.log('Mapping data to success action', success),
        error: (err) => console.log('Error in effect processing:', err),
      })
    )
  );
  */

  fetchReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchReport),
      // tap(() => console.log('Effect running for fetchReport')),
      switchMap(({ reportId }) =>
        this.searchService.fetchReport(reportId).pipe(
          map((report) => {
            // Ensure the response structure includes the expected nested data
            if (!report) {
              throw new Error('Fetch report failed: No report data received');
            }
            return fetchReportSuccess({ report: report });
          }),
          catchError((error) => {
            console.error('Report fetch failed', error);
            return of(fetchReportFailure({ error }));
          })
        )
      )
    )
  );

  archiveReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(archiveReport),
      // tap(() => console.log('Effect running for fetchReport')),
      switchMap(({ reportId }) =>
        this.searchService.archiveReport(reportId).pipe(
          map((archived_report) => {
            // Ensure the response structure includes the expected nested data
            if (!archived_report) {
              throw new Error('Archive report failed: No report data received');
            }
            return archiveReportSuccess({ archived_report: archived_report });
          }),
          catchError((error) => {
            console.error('Report archive failed', error);
            return of(archiveReportFailure({ error }));
          })
        )
      )
    )
  );

  restoreReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(restoreReport),
      // tap(() => console.log('Effect running for fetchReport')),
      switchMap(({ reportId }) =>
        this.searchService.restoreReport(reportId).pipe(
          map((restored_report) => {
            // Ensure the response structure includes the expected nested data
            if (!restored_report) {
              throw new Error('Restore report failed: No report data received');
            }
            return restoreReportSuccess({ restored_report: restored_report });
          }),
          catchError((error) => {
            console.error('Report archive failed', error);
            return of(archiveReportFailure({ error }));
          })
        )
      )
    )
  );

  fetchSelfReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchSelfReport),
      // tap(() => console.log('Effect running for fetchReport')),
      switchMap(({ public_profile_url }) =>
        this.searchService.fetchSelfReport(public_profile_url).pipe(
          map((report) => {
            // Ensure the response structure includes the expected nested data
            if (!report) {
              throw new Error(
                'Fetch self report failed: No report data received'
              );
            }
            return fetchSelfReportSuccess({ report: report });
          }),
          catchError((error) => {
            // console.log('Report self fetch failed', error);
            return of(fetchSelfReportFailure({ error }));
          })
        )
      )
    )
  );

  /* old
  newSearch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(newSearch),
      tap(() => console.log('New search effect triggered')),
      switchMap(({user_id, linkedin_profile}) =>
        this.searchService.newSearch(user_id, linkedin_profile).pipe(
          tap(() => console.log('Calling all searches service', user_id)),
          map(response => {
            if (!response || !response.searches) {
              // If the response object is not structured as expected, throw an error
              throw new Error('Invalid all searches response');
            }
            console.log('All searches successful', response.searches);
            return allSearchesSuccess({searches: response.searches});
          }),
          catchError(error => {
            console.log('Login failed', error);
            return of(allSearchesFailure({error}));
          })
        )
      )
    )
  );
  */

  allNotes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(allNotes),
      // tap(() => console.log('All searches effect triggered')),
      switchMap(({ search_id }) =>
        this.noteService.showNotes(search_id).pipe(
          // tap(() => console.log('Calling all searches service for page: ' + page)),
          map((response) => {
            if (!response || !response.data) {
              // If the response object is not structured as expected, throw an error
              throw new Error('Invalid all notes response');
            }
            // console.log('All searches successful', response.searches);
            return allNotesSuccess({ notes: response.data });
          }),
          catchError((error) => {
            console.error('Searches failed', error);
            return of(allNotesFailure({ error }));
          })
        )
      )
    )
  );

  assistantHistory$ = createEffect((): any =>
    this.actions$.pipe(
      ofType(assistantHistory),
      switchMap(({ search_id, search_company_id }) =>
        from(this.searchService.getAssistantHistory(search_id, search_company_id)).pipe(
          map((response: any) => {
            // Axios wraps the response in a data property
            const actualData = response?.data?.data || response?.data;

            if (!actualData) {
              console.error('[Effect] Invalid response structure:', response);
              throw new Error('Invalid assistant history response');
            }
            return assistantHistorySuccess({ history: actualData });
          }),
          catchError((error) => {
            console.error('[Effect] Assistant history failed:', error);
            return of(assistantHistoryFailure({ error }));
          })
        )
      )
    )
  );

  askAssistant$ = createEffect((): any =>
    this.actions$.pipe(
      ofType(askAssistant),
      switchMap(({ prompt, data, search_id, search_company_id }) =>
        from(this.searchService.askAssistant(prompt, data, search_id, search_company_id)).pipe(
          map((response: any) => {
            // Axios wraps the response in a data property
            const actualData = response?.data?.data || response?.data;

            if (!actualData) {
              console.error('[Effect] Invalid response structure:', response);
              throw new Error('Invalid ask assistant response');
            }
            return askAssistantSuccess({ response: actualData });
          }),
          catchError((error) => {
            console.error('[Effect] Ask assistant failed:', error);
            return of(askAssistantFailure({ error }));
          })
        )
      )
    )
  );

  deleteNote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteNote),
      switchMap(({ note_id, search_id }) =>
        this.noteService.deleteNote(note_id, search_id).pipe(
          map((response) => {
            if (!response || !response.data) {
              throw new Error('Invalid delete notes response');
            }
            return deleteNoteSuccess({ deleted_note: response.data });
          }),
          catchError((error) => {
            console.error('Delete note failed', error);
            return of(deleteNoteFailure({ error }));
          })
        )
      )
    )
  );

  createNote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createNote),
      switchMap(({ user_id, search_id, content }) =>
        this.noteService.createNote(user_id, search_id, content).pipe(
          map((response) => {
            if (!response || !response.data) {
              throw new Error('Invalid create notes response');
            }
            return createNoteSuccess({ created_note: response.data });
          }),
          catchError((error) => {
            console.error('Create note failed', error);
            return of(createNoteFailure({ error }));
          })
        )
      )
    )
  );

  getNotesSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getNotesSummary),
      switchMap(({ search_id, user_id }) =>
        this.noteService.getNotesSummary(search_id, user_id).pipe(
          map((response) => {
            if (!response || !response.data) {
              throw new Error('Invalid getNotesSummary response');
            }
            return getNotesSummarySuccess({ notes_summary: response.data });
          }),
          catchError((error) => {
            console.error('getNotesSummary failed', error);
            return of(getNotesSummaryFailure({ error }));
          })
        )
      )
    )
  );

  showMoreNotes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(showMoreNotes), // Listen for the hubspot Callback action
      switchMap(({ search_id, page }) =>
        this.noteService.showMoreNotes(search_id, page).pipe(
          map((response) => {
            if (!response || !response.data) {
              throw new Error('Invalid Hubspot response');
            }
            return showMoreNotesSuccess({ notes: response.data });
          }),
          catchError((error) => {
            console.error(' Show More Notes failed', error);
            return of(showMoreNotesFailure({ error }));
          })
        )
      )
    )
  );

  editNote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(editNote),
      switchMap(({ note, edited_note_text }) =>
        this.noteService.editNote(note, edited_note_text).pipe(
          map((response) => {
            if (!response || !response.data) {
              throw new Error('Invalid edit notes response');
            }
            return editNoteSuccess({ edited_note: response.data });
          }),
          catchError((error) => {
            console.error('edit note failed', error);
            return of(editNoteFailure({ error }));
          })
        )
      )
    )
  );

  fuzzySearch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fuzzySearch),
      switchMap((action) => {
        // The action payload is now normalized to always have 'params'
        const params = (action as any).params;

        return this.searchService.fuzzySearch(params).pipe(
          map((response) => fuzzySearchSuccess({ results: response.data })),
          catchError((error) => of(fuzzySearchFailure({ error })))
        );
      })
    )
  );

  enrichProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(enrichProfile),
      switchMap(({ searchId }) =>
        this.searchService.enrichProfile(searchId).pipe(
          map((response) => enrichProfileSuccess({
            data: response.data,
            message: response.message
          })),
          catchError((error) => {
            console.error('Profile enrichment failed', error);
            return of(enrichProfileFailure({ error }));
          })
        )
      )
    )
  );

  updateReportAfterEnrichment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(enrichProfileSuccess),
      withLatestFrom(this.store.select(selectReport)),
      map(([action, currentReport]) => {
        if (currentReport && currentReport.report) {
          const updatedReport = {
            ...currentReport.report,
            is_enrich: 1
          };
          return updateReport({ report: { report: updatedReport } });
        }
        return { type: 'NO_ACTION' };
      }),
      filter(action => action.type !== 'NO_ACTION')
    )
  );

  generateInsights$ = createEffect(() =>
    this.actions$.pipe(
      ofType(generateInsights),
      switchMap(({ searchId, insightType }) =>
        this.searchService.generateInsights(searchId, insightType).pipe(
          map((response) => generateInsightsSuccess({
            data: response.data,
            insightType: insightType,
            message: response.message
          })),
          catchError((error) => {
            console.error('Insights generation failed', error);
            return of(generateInsightsFailure({
              error: error.error || error,
              insightType: insightType
            }));
          })
        )
      )
    )
  );

  updateRevenue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateRevenue),
      switchMap(({ searchId, searchCompanyId, revenue, longDescription }) =>
        this.searchService.updateRevenue(searchId, searchCompanyId, revenue, longDescription).pipe(
          map((response) => {
            return updateRevenueSuccess({
              data: response.data,
              message: response.message
            });
          }),
          catchError((error) => {
            console.error('Revenue update failed', error);
            return of(updateRevenueFailure({
              error: error.error || error
            }));
          })
        )
      )
    )
  );

  updateContactInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateContactInfo),
      switchMap(({ searchId, contactType, emails, phones }) =>
        this.searchService.updateContactInfo(searchId, contactType, emails, phones).pipe(
          map((response) => {
            return updateContactInfoSuccess({
              data: response.data,
              message: response.message
            });
          }),
          catchError((error) => {
            console.error('Contact info update failed', error);
            return of(updateContactInfoFailure({
              error: error.error || error
            }));
          })
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private searchService: SearchService,
    private hubspotService: HubspotService,
    private noteService: NoteService,
    private store: Store
  ) {}
}
